import React, { useCallback, useEffect, useState } from "react";
import { posAPI } from "../../services/api";
import { useI18n } from "../../i18n";
import { formatMoney } from "../utils/money";
import { toDoseItem } from "../utils/doses";
import { productImageUrl } from "../utils/images";
import Sheet from "./Sheet";
import ConfirmDialog from "./ConfirmDialog";

/** The liquid's picture, so two bottles with long, similar names are told apart at a glance. */
const Thumb = ({ src, size }) => {
  const [failed, setFailed] = useState(false);
  const url = failed ? null : productImageUrl(src);

  return (
    <span
      className={`shrink-0 ${size} rounded-xl bg-slate-700 flex items-center justify-center overflow-hidden text-xl`}
    >
      {url ? (
        <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} className="w-full h-full object-cover" />
      ) : (
        "💧"
      )}
    </span>
  );
};

const HOUR = 60 * 60 * 1000;

/** How long a bottle lasted: days, or hours under a day, or minutes under an hour. */
const lasted = (summary, t) => {
  const ms = new Date(summary.finished_at) - new Date(summary.opened_at);
  if (!(ms >= 0)) return null;
  if (ms < HOUR) return t("doses.summary.minutes", { count: Math.max(1, Math.round(ms / 60000)) });
  if (ms < 24 * HOUR) return t("doses.summary.hours", { count: Math.round(ms / HOUR) });
  return t("doses.summary.days", { count: Math.round(ms / (24 * HOUR)) });
};

/**
 * What a finished bottle gave: doses, how long it lasted and what it brought in.
 * The server adds what it cost and what it left only for someone who may see buy
 * prices, so the card shows those lines only when they are there.
 */
const BottleSummary = ({ summary, currency, onClose }) => {
  const { t } = useI18n();
  const withCosts = "bottle_cost" in summary;
  const duration = lasted(summary, t);

  const Row = ({ label, value, tone = "text-white" }) => (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <dt className="text-sm text-slate-400">{label}</dt>
      <dd className={`font-semibold tabular-nums ${tone}`}>{value}</dd>
    </div>
  );
  const money = (value) => <span dir="ltr">{formatMoney(value, currency)}</span>;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-5">
      <button
        type="button"
        aria-label={t("common.close")}
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-5">
        <h3 className="text-lg font-bold text-white">{t("doses.summary.title")}</h3>
        <p className="text-sm text-slate-300 mt-1">
          {summary.name}
          {summary.variant_label ? ` · ${summary.variant_label}` : ""}
        </p>
        <dl className="mt-3 divide-y divide-slate-800">
          <Row label={t("doses.summary.doses")} value={summary.doses_sold} />
          {duration && <Row label={t("doses.summary.lasted")} value={duration} />}
          <Row label={t("doses.summary.revenue")} value={money(summary.revenue)} tone="text-emerald-400" />
          {withCosts && (
            <Row
              label={t("doses.summary.cost")}
              value={summary.bottle_cost === null ? t("doses.summary.unknown") : money(summary.bottle_cost)}
            />
          )}
          {withCosts && (
            <Row
              label={t("doses.summary.margin")}
              value={summary.margin === null ? t("doses.summary.unknown") : money(summary.margin)}
              tone={
                summary.margin === null ? "text-slate-400" : summary.margin >= 0 ? "text-emerald-400" : "text-red-400"
              }
            />
          )}
        </dl>
        <p className="text-xs text-slate-500 mt-2">{t("doses.summary.note")}</p>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 h-12 w-full rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-500"
        >
          {t("doses.summary.done")}
        </button>
      </div>
    </div>
  );
};

/**
 * 💧 Selling by the dose: a pod tank filled from an open bottle.
 *
 * Open bottles come first, as big tiles — one tap is one dose on the ticket, and
 * the sheet stays open for the next. Below them, the products sold by the dose
 * that can be opened: opening takes one bottle off the shelf there and then.
 * "Bottle finished" closes one; how many doses it gave is what the shop learns
 * its doses per bottle (and a dose's cost) from.
 */
const DoseSheet = ({ open, onClose, currency, onAddDose, quantityOf, onChanged }) => {
  const { t } = useI18n();
  const [state, setState] = useState({ open: [], products: [] });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [finishing, setFinishing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await posAPI.doses();
      setState({ open: data.open || [], products: data.products || [] });
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || t("doses.failed"));
    }
  }, [t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const openBottle = async (product) => {
    setBusy(product.id);
    try {
      await posAPI.openBottle(product.id);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || t("doses.failed"));
    } finally {
      setBusy(null);
    }
  };

  const finishBottle = async () => {
    const bottle = finishing;
    setFinishing(null);
    setBusy(bottle.id);
    try {
      const { data } = await posAPI.finishBottle(bottle.id);
      setSummary(data);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || t("doses.failed"));
    } finally {
      setBusy(null);
    }
  };

  // Opened by mistake and nothing poured: the bottle goes back on the shelf
  const cancelBottle = async () => {
    const bottle = cancelling;
    setCancelling(null);
    setBusy(bottle.id);
    try {
      await posAPI.cancelBottle(bottle.id);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || t("doses.failed"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={`💧 ${t("doses.title")}`} subtitle={t("doses.subtitle")}>
      <div className="px-4 py-4 space-y-6">
        {error && (
          <p role="alert" className="rounded-xl bg-red-500/15 border border-red-500/40 px-3 py-2 text-sm text-red-200">
            {error}
          </p>
        )}

        <section>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{t("doses.openBottles")}</h3>
          {state.open.length === 0 ? (
            <p className="text-sm text-slate-400">{t("doses.noneOpen")}</p>
          ) : (
            <ul className="grid grid-cols-1 xs:grid-cols-2 gap-2">
              {state.open.map((bottle) => {
                const onTicket = quantityOf(`dose:${bottle.product_id}`);
                return (
                  // A column, so "Bottle finished" sits at the bottom of every card in a row
                  <li
                    key={bottle.id}
                    className="flex flex-col rounded-2xl bg-slate-800 border border-slate-700 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => onAddDose(toDoseItem(bottle))}
                      className="flex-1 flex items-start gap-3 w-full text-start px-4 py-3 hover:bg-slate-700 active:scale-[.98] transition-all"
                    >
                      <Thumb src={bottle.image} size="w-14 h-14" />
                      <span className="min-w-0 flex-1">
                        {/* The name gets the full width; the price sits under it */}
                        <span className="block font-semibold text-white leading-tight">
                          {bottle.name}
                          {bottle.variant_label ? ` · ${bottle.variant_label}` : ""}
                        </span>
                        <span className="block mt-1 font-bold text-emerald-400 tabular-nums">
                          <span dir="ltr">{formatMoney(bottle.dose_price, currency)}</span>
                        </span>
                        <span className="block mt-0.5 text-xs text-slate-400">
                          {t("doses.sold", { count: bottle.doses_sold })}
                          {bottle.doses_per_bottle
                            ? ` · ${t("doses.perBottle", { count: bottle.doses_per_bottle })}`
                            : ""}
                        </span>
                        {onTicket > 0 && (
                          <span className="inline-block mt-2 rounded-full bg-primary-500 px-2 py-0.5 text-xs font-bold text-white">
                            {t("doses.added", { count: onTicket })}
                          </span>
                        )}
                      </span>
                    </button>
                    <div className="flex border-t border-slate-700">
                      {/* Nothing poured yet (and none waiting on the ticket): it can go back */}
                      {bottle.doses_sold === 0 && onTicket === 0 && (
                        <button
                          type="button"
                          disabled={busy === bottle.id}
                          onClick={() => setCancelling(bottle)}
                          className="flex-1 border-e border-slate-700 px-3 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-700 disabled:opacity-60"
                        >
                          {t("doses.cancel")}
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busy === bottle.id}
                        onClick={() => setFinishing(bottle)}
                        className="flex-1 px-3 py-2 text-xs font-semibold text-amber-300 hover:bg-slate-700 disabled:opacity-60"
                      >
                        {busy === bottle.id ? t("doses.finishing") : t("doses.finish")}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">{t("doses.openTitle")}</h3>
          <p className="text-xs text-slate-500 mb-2">{t("doses.openHint")}</p>
          {state.products.length === 0 ? (
            <p className="text-sm text-slate-400">{t("doses.noProducts")}</p>
          ) : (
            <ul className="divide-y divide-slate-800 rounded-2xl border border-slate-800">
              {state.products.map((product) => (
                <li key={product.id} className="flex items-center gap-3 px-4 py-2.5">
                  <Thumb src={product.image} size="w-10 h-10" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-white truncate">
                      {product.name}
                      {product.variant_label ? ` · ${product.variant_label}` : ""}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {product.stock_quantity > 0
                        ? t("doses.left", { count: product.stock_quantity })
                        : t("doses.noneLeft")}
                    </span>
                  </span>
                  <button
                    type="button"
                    disabled={busy === product.id || product.stock_quantity <= 0}
                    onClick={() => openBottle(product)}
                    className="shrink-0 rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500 disabled:opacity-40"
                  >
                    {busy === product.id ? t("doses.opening") : t("doses.open")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {finishing && (
        <ConfirmDialog
          open
          title={t("doses.finish")}
          message={t("doses.finishConfirm", {
            name: finishing.name,
            count: finishing.doses_sold,
          })}
          confirmLabel={t("doses.finish")}
          onConfirm={finishBottle}
          onCancel={() => setFinishing(null)}
        />
      )}

      {cancelling && (
        <ConfirmDialog
          open
          title={t("doses.cancel")}
          message={t("doses.cancelConfirm", { name: cancelling.name })}
          confirmLabel={t("doses.cancel")}
          onConfirm={cancelBottle}
          onCancel={() => setCancelling(null)}
        />
      )}

      {summary && <BottleSummary summary={summary} currency={currency} onClose={() => setSummary(null)} />}
    </Sheet>
  );
};

export default DoseSheet;
